/**
 * `growth/placements` — typed registry of placements + **legality guard**.
 *
 * The registry is the source of truth for gates, tests, and audit: if a
 * placement is not declared, it does not exist. The [`validatePlacements`] guard
 * is what prevents a builder from slipping an interstitial into a B2B app (tier C) or a
 * native ad outside a breakpoint where it makes sense — and it is a TEST, not a
 * convention written in a guide nobody rereads.
 */

import type { AdBreakpointId, AdKind, AdTier, PlacementDef, PlacementRegistry } from "./types";

/**
 * Declaration helper: preserves LITERAL ids (so
 * `AD_PLACEMENTS["list-done"]` is a type error if the id does not exist) but
 * widens values to `PlacementDef` — otherwise `reward` would be narrowed to
 * the union of literals from each entry and consuming it generically would be a
 * type error in every clone.
 */
export function definePlacements<const T extends PlacementRegistry>(
  registry: T,
): { [K in keyof T]: PlacementDef } {
  return registry;
}

/** Legal formats per app tier (§9 of the strategy). */
export const LEGAL_KINDS_BY_TIER: Record<AdTier, readonly AdKind[]> = {
  // A — consumer: all formats, with the general capping.
  A: ["rewarded", "native", "interstitial", "banner"],
  // K — child-oriented: banner G + soft interstitial; rewarded exists but only
  // after the adult gate (`adGate` applies it; here it is only declared legal).
  K: ["banner", "interstitial", "rewarded"],
  // C — B2B/institutional: ZERO ads.
  C: [],
  // D — privacy: ZERO ads and zero embedded SDK.
  D: [],
};

/** Breakpoints where each format makes sense (§2 and §3 of the strategy). */
export const LEGAL_BREAKPOINTS_BY_KIND: Record<AdKind, readonly AdBreakpointId[]> = {
  // Full-screen ONLY at a breather point, never in the middle of a task.
  interstitial: ["done-screen", "session-end", "between-screens"],
  // Integrated card: long feeds and passive consumer screens.
  native: ["stats", "feed-slot", "done-screen"],
  // Fixed strip: passive screens only.
  banner: ["stats", "session-end"],
  // Opt-in offer: alongside the app's economy.
  rewarded: ["reward-moment", "done-screen", "session-end"],
};

export interface PlacementViolation {
  placementId: string;
  reason:
    | "kind-illegal-for-tier"
    | "breakpoint-illegal-for-kind"
    | "rewarded-without-reward"
    | "reward-on-non-rewarded";
  detail: string;
}

/**
 * Verifies that EVERY declared placement is legal for app tier.
 * Returns list of violations (empty = all good) so test can
 * display all at once instead of failing on first.
 */
export function validatePlacements(
  registry: PlacementRegistry,
  tier: AdTier,
): PlacementViolation[] {
  const violations: PlacementViolation[] = [];
  const legalKinds = LEGAL_KINDS_BY_TIER[tier];

  for (const [placementId, def] of Object.entries(registry)) {
    if (!legalKinds.includes(def.kind)) {
      violations.push({
        placementId,
        reason: "kind-illegal-for-tier",
        detail: `'${def.kind}' no está permitido en el tier ${tier}`,
      });
    }
    if (!LEGAL_BREAKPOINTS_BY_KIND[def.kind].includes(def.breakpoint)) {
      violations.push({
        placementId,
        reason: "breakpoint-illegal-for-kind",
        detail: `'${def.kind}' no puede vivir en el breakpoint '${def.breakpoint}'`,
      });
    }
    if (def.kind === "rewarded" && !def.reward) {
      violations.push({
        placementId,
        reason: "rewarded-without-reward",
        detail: "un rewarded debe declarar QUÉ se gana (se enseña antes de verlo)",
      });
    }
    if (def.kind !== "rewarded" && def.reward) {
      violations.push({
        placementId,
        reason: "reward-on-non-rewarded",
        detail: `solo un rewarded otorga recompensa ('${def.kind}' no)`,
      });
    }
  }
  return violations;
}

/** Test/bootstrap sugar: throws with detail if there are violations. */
export function assertPlacementsLegal(registry: PlacementRegistry, tier: AdTier): void {
  const violations = validatePlacements(registry, tier);
  if (violations.length > 0) {
    const detail = violations.map((v) => `  · ${v.placementId}: ${v.detail}`).join("\n");
    throw new Error(`Illegal placements for tier ${tier}:\n${detail}`);
  }
}

/** All placements of a given format (useful for e2e suites). */
export function placementsOfKind(registry: PlacementRegistry, kind: AdKind): string[] {
  return Object.entries(registry)
    .filter(([, def]) => def.kind === kind)
    .map(([id]) => id);
}

/** Safe lookup: `undefined` if the id is not in the registry. */
export function placementDef(
  registry: PlacementRegistry,
  placementId: string,
): PlacementDef | undefined {
  return registry[placementId];
}
